import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState, useCallback, useRef } from 'react';
import { checkForAppUpdate } from '../serviceWorker';

const REFRESH_THRESHOLD_PX = 80;
const MAX_PULL_DISTANCE_PX = 120;

export const useRefresh = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();
  const router = useRouter();

  const refreshAllData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await checkForAppUpdate();

      // Invalidate all React Query data and reload the current TanStack Router route.
      await Promise.all([queryClient.invalidateQueries(), router.invalidate()]);
    } catch (error) {
      console.error('Failed to refresh data:', error);
      const event = new CustomEvent('showToast', {
        detail: { message: 'Failed to refresh page', type: 'error' },
      });
      window.dispatchEvent(event);
    } finally {
      setIsRefreshing(false);
    }
  }, [queryClient, router]);

  return { refreshAllData, isRefreshing };
};

export const usePullToRefresh = () => {
  const { refreshAllData, isRefreshing } = useRefresh();
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const touchStartY = useRef<number | null>(null);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    const target = e.target as Element | null;

    // Horizontal controls such as the dashboard week picker manage their own
    // touch gestures. Starting pull-to-refresh there can cancel the synthetic
    // click that browsers emit after a tap, making the date buttons unusable.
    if (target?.closest('[data-disable-pull-to-refresh]')) {
      touchStartY.current = null;
      setIsPulling(false);
      return;
    }

    const scrollContainer = target?.closest('main');
    const isAtTop = scrollContainer
      ? scrollContainer.scrollTop === 0
      : window.scrollY === 0;

    if (isAtTop && e.touches.length === 1) {
      touchStartY.current = e.touches[0].clientY;
      setIsPulling(true);
    }
  }, []);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (
        !isPulling ||
        touchStartY.current === null ||
        e.touches.length !== 1
      ) {
        return;
      }

      const distance = Math.max(0, e.touches[0].clientY - touchStartY.current);

      if (distance > 0) {
        e.preventDefault();
      }

      setPullDistance(Math.min(distance, MAX_PULL_DISTANCE_PX));
    },
    [isPulling],
  );

  const handleTouchEnd = useCallback(async () => {
    if (isPulling && pullDistance > REFRESH_THRESHOLD_PX && !isRefreshing) {
      await refreshAllData();
    }

    touchStartY.current = null;
    setIsPulling(false);
    setPullDistance(0);
  }, [isPulling, isRefreshing, pullDistance, refreshAllData]);

  return {
    isPulling,
    pullDistance,
    isRefreshing,
    refreshThreshold: REFRESH_THRESHOLD_PX,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
};
